import type { AuthError } from "@supabase/supabase-js";

/**
 * Mapping des erreurs Supabase vers des messages français compréhensibles
 *
 * Ce mapping couvre tous les codes d'erreur Supabase courants pour l'authentification
 * et fournit des messages clairs et actionnables pour l'utilisateur.
 */

interface ErrorMapping {
  pattern: RegExp | string;
  message: string;
  context?: string;
}

/**
 * Interface pour les erreurs Supabase avec codes d'erreur
 */
interface SupabaseErrorWithCode {
  message?: string;
  error_description?: string;
  status?: number;
  code?: string;
}

/**
 * Mapping des codes d'erreur Supabase vers des messages français
 * Priorité sur le pattern matching car plus fiable
 */
const ERROR_CODE_MAPPINGS: Record<string, string> = {
  // Codes d'erreur Supabase Auth spécifiques
  'invalid_credentials': 'Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.',
  'email_not_confirmed': 'Votre email n\'est pas vérifié. Veuillez vérifier votre boîte de réception et cliquer sur le lien de vérification.',
  'user_not_found': 'Aucun compte trouvé avec cet email. Vérifiez votre adresse email ou créez un compte.',
  'weak_password': 'Le mot de passe ne respecte pas les critères de sécurité. Il doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.',
  'over_request_rate_limit': 'Trop de tentatives. Veuillez réessayer dans quelques minutes.',
  'over_email_send_rate_limit': 'Trop d\'emails envoyés. Veuillez réessayer dans quelques minutes.',
  'signup_disabled': 'Les inscriptions sont temporairement désactivées. Veuillez réessayer plus tard.',
  'email_rate_limit_exceeded': 'Trop de tentatives d\'inscription. Veuillez réessayer dans quelques minutes.',
  'user_already_exists': 'Cet email est déjà associé à un compte. Connectez-vous ou réinitialisez votre mot de passe si vous l\'avez oublié.',
  'email_already_exists': 'Cet email est déjà associé à un compte. Connectez-vous ou réinitialisez votre mot de passe si vous l\'avez oublié.',
  'email_taken': 'Cet email est déjà associé à un compte. Connectez-vous ou réinitialisez votre mot de passe si vous l\'avez oublié.',
  'duplicate_email': 'Cet email est déjà associé à un compte. Connectez-vous ou réinitialisez votre mot de passe si vous l\'avez oublié.',
  'email_in_use': 'Cet email est déjà associé à un compte. Connectez-vous ou réinitialisez votre mot de passe si vous l\'avez oublié.',
  
  // Codes d'erreur personnalisés pour emails dupliqués (ajoutés par RAKB)
  'email_exists_with_oauth': 'Cet email est déjà utilisé avec un compte Google. Connectez-vous avec Google pour accéder à votre compte.',
  'email_exists_with_password': 'Cet email est déjà utilisé avec un mot de passe. Connectez-vous avec votre email et mot de passe, ou utilisez "Mot de passe oublié".',

  // Codes HTTP courants
  '400': 'Requête invalide. Vérifiez les informations saisies.',
  '422': 'Les données fournies ne sont pas valides. Vérifiez votre email et votre mot de passe.',
  '429': 'Trop de tentatives. Veuillez réessayer dans quelques minutes.',
  '500': 'Erreur serveur. Veuillez réessayer plus tard.',

  // Codes d'erreur OAuth
  'validation_failed': 'Erreur de validation. Veuillez vérifier vos informations.',
  'redirect_uri_mismatch': 'L\'URL de redirection ne correspond pas à la configuration. Vérifiez que l\'URL est bien configurée dans Google Cloud Console.',
  'invalid_client': 'Configuration OAuth invalide. Vérifiez les identifiants (Client ID et Secret) dans Supabase Dashboard.',
  'access_denied': 'Accès refusé. Vous avez annulé l\'autorisation ou l\'accès a été refusé.',
  'invalid_grant': 'Le code d\'autorisation est invalide ou a expiré. Veuillez réessayer.',
  'invalid_request': 'Requête OAuth invalide. Vérifiez la configuration.',
  'user_cancelled': 'Vous avez annulé la connexion.',
};

/**
 * Mapping des erreurs d'authentification Supabase
 */
const AUTH_ERROR_MAPPINGS: ErrorMapping[] = [
  // Erreurs personnalisées RAKB pour gestion des emails dupliqués
  {
    pattern: /EMAIL_EXISTS_WITH_DIFFERENT_PROVIDER/i,
    message: "Cet email est déjà utilisé avec un compte Google. Connectez-vous avec Google pour accéder à votre compte.",
    context: "register",
  },
  {
    pattern: /email.*exists.*google|google.*email.*exists/i,
    message: "Cet email est déjà utilisé avec un compte Google. Connectez-vous avec Google pour accéder à votre compte.",
    context: "register",
  },
  {
    pattern: /email.*exists.*password|password.*email.*exists/i,
    message: "Cet email est déjà utilisé avec un mot de passe. Connectez-vous avec votre email et mot de passe, ou utilisez 'Mot de passe oublié'.",
    context: "oauth",
  },
  // Erreurs standard Supabase
  {
    pattern: /invalid.*credentials?/i,
    message: "Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.",
    context: "login",
  },
  {
    pattern: /email.*not.*confirmed?/i,
    message: "Votre email n'est pas vérifié. Veuillez vérifier votre boîte de réception et cliquer sur le lien de vérification.",
    context: "login",
  },
  {
    pattern: /too.*many.*requests?/i,
    message: "Trop de tentatives. Veuillez réessayer dans quelques minutes.",
    context: "rate_limit",
  },
  {
    pattern: /user.*not.*found/i,
    message: "Aucun compte trouvé avec cet email. Vérifiez votre adresse email ou créez un compte.",
    context: "login",
  },
  {
    pattern: /email.*already.*registered|email.*already.*exists|user.*already.*registered|user.*already.*exists|email.*taken|duplicate.*email|email.*in.*use/i,
    message: "Cet email est déjà associé à un compte. Connectez-vous ou réinitialisez votre mot de passe si vous l'avez oublié.",
    context: "register",
  },
  {
    pattern: /déjà.*utilisé|existe.*déjà|déjà.*enregistré|déjà.*associé/i,
    message: "Cet email est déjà associé à un compte. Connectez-vous ou réinitialisez votre mot de passe si vous l'avez oublié.",
    context: "register",
  },
  {
    pattern: /signup_disabled|signup.*disabled/i,
    message: "Les inscriptions sont temporairement désactivées. Veuillez réessayer plus tard.",
    context: "register",
  },
  {
    pattern: /email.*rate.*limit|signup.*rate.*limit/i,
    message: "Trop de tentatives d'inscription. Veuillez réessayer dans quelques minutes.",
    context: "register",
  },
  {
    pattern: /weak.*password|password.*too.*weak/i,
    message: "Le mot de passe ne respecte pas les critères de sécurité. Il doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.",
    context: "password",
  },
  {
    pattern: /invalid.*email/i,
    message: "L'adresse email n'est pas valide. Veuillez vérifier le format de votre email.",
    context: "email",
  },
  {
    pattern: /token.*expired/i,
    message: "Le lien de réinitialisation a expiré. Veuillez en demander un nouveau.",
    context: "password_reset",
  },
  {
    pattern: /invalid.*token/i,
    message: "Le lien de réinitialisation est invalide. Veuillez en demander un nouveau.",
    context: "password_reset",
  },
  {
    pattern: /session.*not.*found|session.*expired/i,
    message: "Votre session a expiré. Veuillez vous reconnecter.",
    context: "session",
  },
  {
    pattern: /token.*refresh.*failed/i,
    message: "Impossible de rafraîchir votre session. Veuillez vous reconnecter.",
    context: "session",
  },
  {
    pattern: /unauthorized|forbidden/i,
    message: "Vous n'êtes pas autorisé à effectuer cette action.",
    context: "permission",
  },
  {
    pattern: /network.*error|failed.*to.*fetch/i,
    message: "Erreur de connexion. Vérifiez votre connexion internet et réessayez.",
    context: "network",
  },
  {
    pattern: /timeout|timed.*out/i,
    message: "La requête a pris trop de temps. Veuillez réessayer.",
    context: "network",
  },
  {
    pattern: /service.*unavailable/i,
    message: "Le service est temporairement indisponible. Veuillez réessayer plus tard.",
    context: "server",
  },
  {
    pattern: /row-level.*security|RLS/i,
    message: "Erreur de sécurité : Impossible d'accéder à cette ressource. Veuillez contacter le support si le problème persiste.",
    context: "security",
  },
  {
    pattern: /unsupported.*provider|provider.*not.*enabled|provider.*is.*not.*enabled/i,
    message: "Le fournisseur OAuth n'est pas activé. Veuillez contacter le support pour activer cette fonctionnalité.",
    context: "oauth",
  },
  {
    pattern: /oauth.*error|oauth.*failed/i,
    message: "Erreur lors de la connexion avec le fournisseur OAuth. Veuillez réessayer ou utiliser une autre méthode de connexion.",
    context: "oauth",
  },
  {
    pattern: /redirect.*uri.*mismatch|redirect.*url.*mismatch/i,
    message: "L'URL de redirection ne correspond pas à la configuration. Vérifiez que l'URL est bien configurée dans Google Cloud Console (Authorized redirect URIs).",
    context: "oauth",
  },
  {
    pattern: /invalid.*client|client.*invalid/i,
    message: "Configuration OAuth invalide. Vérifiez que le Client ID et le Client Secret sont correctement configurés dans Supabase Dashboard (Authentication > Providers > Google).",
    context: "oauth",
  },
  {
    pattern: /access.*denied|user.*denied/i,
    message: "Accès refusé. Vous avez annulé l'autorisation ou l'accès a été refusé par le fournisseur OAuth.",
    context: "oauth",
  },
  {
    pattern: /invalid.*grant|grant.*invalid/i,
    message: "Le code d'autorisation est invalide ou a expiré. Veuillez réessayer la connexion.",
    context: "oauth",
  },
  {
    pattern: /unexpected.*token|JSON\.parse|syntaxerror/i,
    message: "Erreur de communication avec le serveur. Veuillez réessayer dans quelques instants.",
    context: "network",
  },
];

/**
 * Obtient un message d'erreur français à partir d'une erreur Supabase
 *
 * @param error - L'erreur Supabase (AuthError ou Error)
 * @param defaultMessage - Message par défaut si aucune correspondance n'est trouvée
 * @returns Message d'erreur en français
 *
 * @example
 * ```typescript
 * try {
 *   await supabase.auth.signInWithPassword({ email, password });
 * } catch (error) {
 *   const message = getSupabaseErrorMessage(error, "Une erreur est survenue");
 *   toast.error(message);
 * }
 * ```
 */
export function getSupabaseErrorMessage(
  error: AuthError | Error | unknown,
  defaultMessage: string = "Une erreur est survenue. Veuillez réessayer."
): string {
  if (!error) {
    return defaultMessage;
  }

  // Extraire les informations de l'erreur (message, code, status)
  let errorMessage = "";
  let errorCode: string | undefined;
  let errorStatus: number | undefined;

  if (error instanceof Error) {
    errorMessage = error.message;
    // Vérifier si l'erreur a des propriétés supplémentaires (AuthError)
    const authError = error as unknown as SupabaseErrorWithCode;
    errorCode = authError.code;
    errorStatus = authError.status;
  } else if (typeof error === "string") {
    errorMessage = error;
  } else if (typeof error === "object" && error !== null) {
    // Pour les erreurs Supabase AuthError
    const authError = error as SupabaseErrorWithCode;
    errorMessage = authError.message || authError.error_description || "";
    errorCode = authError.code;
    errorStatus = authError.status;
  }

  // PRIORITÉ 1 : Vérifier les codes d'erreur (plus fiable que le pattern matching)
  if (errorCode && ERROR_CODE_MAPPINGS[errorCode]) {
    // Cas spécial : validation_failed avec message "Unsupported provider"
    if (errorCode === 'validation_failed' && errorMessage) {
      const normalizedMessage = errorMessage.toLowerCase();
      if (normalizedMessage.includes('unsupported provider') || normalizedMessage.includes('provider is not enabled')) {
        return "Le fournisseur OAuth n'est pas activé dans Supabase. Vérifiez la configuration dans le Dashboard Supabase (Authentication > Providers > Google) et assurez-vous que le provider est activé avec les identifiants corrects.";
      }
    }
    return ERROR_CODE_MAPPINGS[errorCode];
  }

  // PRIORITÉ 2 : Chercher une correspondance dans le mapping par pattern.
  // Doit passer avant le statut HTTP : un 400 couvre aussi bien des identifiants
  // invalides qu'un email non confirmé, le message est plus précis.
  const normalizedMessage = errorMessage.toLowerCase();

  for (const mapping of AUTH_ERROR_MAPPINGS) {
    if (typeof mapping.pattern === "string") {
      if (normalizedMessage.includes(mapping.pattern.toLowerCase())) {
        return mapping.message;
      }
    } else {
      // C'est une RegExp
      if (mapping.pattern.test(errorMessage) || mapping.pattern.test(normalizedMessage)) {
        return mapping.message;
      }
    }
  }

  // PRIORITÉ 3 : Vérifier les codes HTTP (générique, en dernier recours)
  if (errorStatus && ERROR_CODE_MAPPINGS[String(errorStatus)]) {
    return ERROR_CODE_MAPPINGS[String(errorStatus)];
  }

  if (!errorMessage) {
    return defaultMessage;
  }

  // Si aucune correspondance, retourner le message d'erreur original
  // mais essayer de le nettoyer un peu
  const cleanedMessage = errorMessage
    .replace(/^Error:\s*/i, "")
    .replace(/^AuthApiError:\s*/i, "")
    .trim();

  // Si le message nettoyé est vide ou trop technique, utiliser le message par défaut
  if (!cleanedMessage || cleanedMessage.length < 3 || cleanedMessage.includes("{")) {
    return defaultMessage;
  }

  return cleanedMessage;
}

/** Keys for i18n (errors.xxx) - used when services throw and UI calls t(message) */
const ERROR_CODE_KEYS: Record<string, string> = {
  invalid_credentials: "errors.auth.invalidCredentials",
  email_not_confirmed: "errors.auth.emailNotVerified",
  user_not_found: "errors.auth.invalidCredentials",
  weak_password: "errors.auth.weakPassword",
  over_request_rate_limit: "errors.auth.tooManyRequests",
  over_email_send_rate_limit: "errors.auth.tooManyRequests",
  signup_disabled: "errors.auth.signupFailed",
  email_rate_limit_exceeded: "errors.auth.tooManyRequests",
  user_already_exists: "errors.auth.emailAlreadyExists",
  email_already_exists: "errors.auth.emailAlreadyExists",
  email_taken: "errors.auth.emailAlreadyExists",
  duplicate_email: "errors.auth.emailAlreadyExists",
  email_in_use: "errors.auth.emailAlreadyExists",
  email_exists_with_oauth: "errors.auth.emailExistsWithOAuth",
  email_exists_with_password: "errors.auth.emailExistsWithPassword",
  "400": "errors.generic",
  "422": "errors.auth.invalidCredentials",
  "429": "errors.auth.tooManyRequests",
  "500": "errors.generic",
  validation_failed: "errors.auth.loginFailed",
  access_denied: "errors.auth.sessionExpired",
  user_cancelled: "errors.auth.sessionExpired",
};

const AUTH_ERROR_KEY_MAPPINGS: { pattern: RegExp | string; key: string }[] = [
  { pattern: /EMAIL_EXISTS_WITH_DIFFERENT_PROVIDER/i, key: "errors.auth.emailExistsWithOAuth" },
  { pattern: /email.*exists.*google|google.*email.*exists/i, key: "errors.auth.emailExistsWithOAuth" },
  { pattern: /email.*exists.*password|password.*email.*exists/i, key: "errors.auth.emailExistsWithPassword" },
  { pattern: /invalid.*credentials?/i, key: "errors.auth.invalidCredentials" },
  { pattern: /email.*not.*confirmed?|EMAIL_NOT_VERIFIED/i, key: "errors.auth.emailNotVerified" },
  { pattern: /too.*many.*requests?/i, key: "errors.auth.tooManyRequests" },
  { pattern: /user.*not.*found/i, key: "errors.auth.invalidCredentials" },
  { pattern: /weak.*password|password.*too.*weak/i, key: "errors.auth.weakPassword" },
  { pattern: /email.*already.*registered|email.*already.*exists|user.*already.*registered|user.*already.*exists|email.*taken|duplicate.*email|email.*in.*use/i, key: "errors.auth.emailAlreadyExists" },
  { pattern: /déjà.*utilisé|existe.*déjà|déjà.*enregistré|déjà.*associé/i, key: "errors.auth.emailAlreadyExists" },
  { pattern: /signup_disabled|signup.*disabled/i, key: "errors.auth.signupFailed" },
  { pattern: /email.*rate.*limit|signup.*rate.*limit/i, key: "errors.auth.tooManyRequests" },
  { pattern: /session.*not.*found|session.*expired|token.*refresh.*failed/i, key: "errors.auth.sessionExpired" },
  { pattern: /network.*error|failed.*to.*fetch/i, key: "errors.network" },
  { pattern: /timeout|timed.*out/i, key: "errors.timeout" },
];

/**
 * Returns an i18n key for the error so the UI can call t(key).
 * Use this in services when you want the message translated in the current language.
 */
export function getSupabaseErrorKey(
  error: AuthError | Error | unknown,
  defaultKey: string = "errors.generic"
): string {
  if (!error) return defaultKey;

  let errorMessage = "";
  let errorCode: string | undefined;
  let errorStatus: number | undefined;

  if (error instanceof Error) {
    errorMessage = error.message;
    const authError = error as unknown as SupabaseErrorWithCode;
    errorCode = authError.code;
    errorStatus = authError.status;
  } else if (typeof error === "string") {
    errorMessage = error;
  } else if (typeof error === "object" && error !== null) {
    const authError = error as SupabaseErrorWithCode;
    errorMessage = authError.message || authError.error_description || "";
    errorCode = authError.code;
    errorStatus = authError.status;
  }

  if (errorCode && ERROR_CODE_KEYS[errorCode]) {
    return ERROR_CODE_KEYS[errorCode];
  }

  // Le message est analysé avant le statut HTTP : un 400 est trop générique
  // et masquerait des cas précis comme des identifiants invalides.
  const normalizedMessage = errorMessage.toLowerCase();
  for (const mapping of AUTH_ERROR_KEY_MAPPINGS) {
    if (typeof mapping.pattern === "string") {
      if (normalizedMessage.includes(mapping.pattern.toLowerCase())) return mapping.key;
    } else {
      if (mapping.pattern.test(errorMessage) || mapping.pattern.test(normalizedMessage)) return mapping.key;
    }
  }

  if (errorStatus && ERROR_CODE_KEYS[String(errorStatus)]) {
    return ERROR_CODE_KEYS[String(errorStatus)];
  }

  return defaultKey;
}

/**
 * Vérifie si une erreur est une erreur de session expirée
 *
 * @param error - L'erreur à vérifier
 * @returns true si c'est une erreur de session expirée
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const normalized = errorMessage.toLowerCase();

  return (
    normalized.includes("session") &&
    (normalized.includes("expired") || normalized.includes("not found"))
  ) || normalized.includes("token_refresh_failed");
}

/**
 * Vérifie si une erreur est une erreur de rate limiting
 *
 * @param error - L'erreur à vérifier
 * @returns true si c'est une erreur de rate limiting
 */
export function isRateLimitError(error: unknown): boolean {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const normalized = errorMessage.toLowerCase();

  return normalized.includes("too many") || normalized.includes("rate limit");
}

/**
 * Vérifie si une erreur est une erreur réseau
 *
 * @param error - L'erreur à vérifier
 * @returns true si c'est une erreur réseau
 */
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const normalized = errorMessage.toLowerCase();

  return (
    normalized.includes("network") ||
    normalized.includes("failed to fetch") ||
    normalized.includes("timeout") ||
    normalized.includes("timed out")
  );
}

/**
 * Vérifie si une erreur est liée à un email déjà existant
 *
 * @param error - L'erreur à vérifier
 * @returns Un objet indiquant si c'est une erreur d'email existant et avec quel provider
 */
export function isEmailExistsError(error: unknown): { 
  exists: boolean; 
  withOAuth: boolean; 
  withPassword: boolean;
} {
  if (!error) return { exists: false, withOAuth: false, withPassword: false };

  const errorMessage = error instanceof Error ? error.message : String(error);
  const normalized = errorMessage.toLowerCase();

  // Cas spécifique: email existe avec OAuth (Google)
  if (
    normalized.includes("email_exists_with_different_provider") ||
    normalized.includes("email exists with google") ||
    (normalized.includes("google") && normalized.includes("already"))
  ) {
    return { exists: true, withOAuth: true, withPassword: false };
  }

  // Cas spécifique: email existe avec mot de passe
  if (
    normalized.includes("email exists with password") ||
    (normalized.includes("password") && normalized.includes("already"))
  ) {
    return { exists: true, withOAuth: false, withPassword: true };
  }

  // Cas général: email déjà utilisé (sans savoir quel provider)
  if (
    normalized.includes("already registered") ||
    normalized.includes("already exists") ||
    normalized.includes("user_already_exists") ||
    normalized.includes("email_taken") ||
    normalized.includes("email_in_use") ||
    normalized.includes("déjà associé") ||
    normalized.includes("déjà utilisé")
  ) {
    return { exists: true, withOAuth: false, withPassword: false };
  }

  return { exists: false, withOAuth: false, withPassword: false };
}

